/**
 * Les actions prioritaires du cabinet — chunk 9.AF.1.
 *
 * Le tableau de bord répondait à « combien y a-t-il de dossiers ». Il ne répondait
 * pas à la seule question qu'un conseiller se pose en arrivant le matin : par quoi
 * je commence.
 *
 * ## Trois groupes, et pourquoi ce ne sont pas des filtres
 *
 * Le serveur renvoie les échéances de tout le cabinet — délibérément, cf.
 * `apps/api/src/routes/deadlines.ts` : un filtre « seulement les miennes » ferait
 * disparaître les échéances sans responsable de TOUS les tableaux de bord à la
 * fois. C'est donc ici qu'on sépare, et les trois groupes sont montrés ensemble :
 *
 *   1. **Sans responsable** — personne ne s'en occupe, et c'est le seul groupe que
 *      n'importe qui peut débloquer. Il vient en premier pour cette raison, même
 *      quand il n'est pas le plus urgent dans le temps.
 *   2. **À moi** — ce qui m'incombe.
 *   3. **L'équipe** — visible sans être mis en avant : savoir qu'un collègue est
 *      débordé a de la valeur, mais ce n'est pas mon action du jour.
 *
 * Montrer le troisième groupe est un choix : dans un cabinet de trois personnes,
 * masquer le travail des autres crée des angles morts pendant les vacances.
 *
 * ## Aucun recalcul
 *
 * L'état temporel et la prochaine action arrivent qualifiés du serveur. Les
 * recalculer installerait un second seuil de « échéance proche » — un dossier
 * serait « urgent » ici et « à venir » sur sa page.
 */
import type { DeadlineItem, DeadlineState } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { useTenantDeadlines } from "../../lib/queries/deadlines";

const TON: Record<DeadlineState, string> = {
  overdue: "bg-red-50 text-red-800 ring-red-200",
  unassigned: "bg-violet-50 text-violet-800 ring-violet-200",
  due_soon: "bg-amber-50 text-amber-800 ring-amber-200",
  upcoming: "bg-slate-100 text-slate-700 ring-slate-200",
  resolved: "bg-emerald-50 text-emerald-800 ring-emerald-200",
};

/** Ce qu'on montre par groupe. Au-delà, l'écran cesse d'être une priorisation. */
const MAX_PAR_GROUPE = 5;

function Ligne({ item }: { readonly item: DeadlineItem }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("en") ? "en" : "fr";

  return (
    <li className="border-t border-slate-100 py-2.5 first:border-t-0">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        <span
          className={`mt-0.5 inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TON[item.state] ?? TON.upcoming}`}
        >
          {t(`deadlines.state.${item.state}`)}
        </span>
        <span className="min-w-0 flex-1 basis-48">
          {/* 9.BH.1 — le CLIENT d'abord. Une alerte qui ne nomme que le dossier
              oblige à l'ouvrir pour savoir chez qui elle se joue. */}
          {item.company_name && (
            <span className="block text-xs font-medium text-slate-500">{item.company_name}</span>
          )}
          <span className="block text-sm text-slate-900">{item.label}</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
            <Link
              to={`/projects/${item.project_id}`}
              className="focus-visible:ring-brand-500 text-brand-700 rounded hover:underline focus-visible:outline-none focus-visible:ring-2"
            >
              {item.project_title}
            </Link>
            {/* Et QUI l'on attend : « une réponse est attendue » sans le nom de
                l'assureur laisse la même question sans réponse. */}
            {item.awaited_insurers && (
              <>
                <span aria-hidden="true">·</span>
                <span>{t("deadlines.awaiting_from", { insurers: item.awaited_insurers })}</span>
              </>
            )}
            {item.due_at !== null && (
              <>
                <span aria-hidden="true">·</span>
                <span className={item.state === "overdue" ? "font-medium text-red-700" : undefined}>
                  {item.days_remaining !== null && item.days_remaining < 0
                    ? t("deadlines.overdue_by", { count: Math.abs(item.days_remaining) })
                    : t("deadlines.due_on", {
                        date: new Date(item.due_at).toLocaleDateString(lang),
                      })}
                </span>
              </>
            )}
          </span>
        </span>

        {/* Le geste utile, sur la ligne : suivre une demande, ou ouvrir le
            dossier pour tout le reste. Sans lui, l'alerte constate sans mener
            nulle part. */}
        <Link
          to={
            item.subject_kind === "quote_request"
              ? `/projects/${item.project_id}?view=insurers&request=${item.subject_id}`
              : `/projects/${item.project_id}`
          }
          className="focus-visible:ring-brand-500 inline-flex min-h-11 shrink-0 touch-manipulation items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {item.subject_kind === "quote_request"
            ? t("projects.operational.cta.track")
            : t("deadlines.open_file")}
        </Link>
      </div>
    </li>
  );
}

function Groupe({
  titre,
  aide,
  items,
  accent,
}: {
  readonly titre: string;
  readonly aide: string;
  readonly items: readonly DeadlineItem[];
  readonly accent: string;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className={`text-sm font-semibold ${accent}`}>
        {titre} <span className="font-normal text-slate-500">({items.length})</span>
      </h3>
      <p className="mt-0.5 text-xs text-slate-500">{aide}</p>
      <ul className="mt-2">
        {items.slice(0, MAX_PAR_GROUPE).map((i) => (
          <Ligne key={i.id} item={i} />
        ))}
      </ul>
      {items.length > MAX_PAR_GROUPE && (
        // Dire ce qui n'est pas montré : une liste tronquée en silence se lit comme
        // une liste complète.
        <p className="mt-2 text-xs text-slate-500">
          {t("priority.more", { count: items.length - MAX_PAR_GROUPE })}
        </p>
      )}
    </div>
  );
}

export function PriorityActionsSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const req = useTenantDeadlines();

  if (req.isPending) {
    return (
      <section className="mt-10" aria-label={t("priority.title")}>
        <h2 className="text-lg font-semibold text-slate-900">{t("priority.title")}</h2>
        <p className="mt-2 text-sm text-slate-500" aria-live="polite">
          {t("deadlines.loading")}
        </p>
      </section>
    );
  }

  if (req.isError || !req.data || typeof req.data.next_action !== "string") {
    return (
      <section className="mt-10" aria-label={t("priority.title")}>
        <h2 className="text-lg font-semibold text-slate-900">{t("priority.title")}</h2>
        <p
          className="mt-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
          role="alert"
        >
          {t("deadlines.error")}
        </p>
      </section>
    );
  }

  const tous = (Array.isArray(req.data.items) ? req.data.items : []).filter(
    (i) => i?.id && i.state !== "resolved",
  );
  const orphelines = tous.filter((i) => i.responsible_user_id === null);
  const miennes = tous.filter((i) => i.responsible_user_id === user?.id);
  const equipe = tous.filter(
    (i) => i.responsible_user_id !== null && i.responsible_user_id !== user?.id,
  );

  return (
    <section className="mt-10" aria-label={t("priority.title")}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900">{t("priority.title")}</h2>
        <p className="text-sm font-medium text-slate-700">
          {t(`deadlines.next_action.${req.data.next_action}`)}
        </p>
      </div>

      {tous.length === 0 ? (
        <p className="mt-3 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-600">
          {t("priority.empty")}
        </p>
      ) : (
        <div className="mt-3 space-y-4">
          <Groupe
            titre={t("priority.unassigned")}
            aide={t("priority.unassigned_help")}
            items={orphelines}
            accent="text-violet-800"
          />
          <Groupe
            titre={t("priority.mine")}
            aide={t("priority.mine_help")}
            items={miennes}
            accent="text-slate-900"
          />
          <Groupe
            titre={t("priority.team")}
            aide={t("priority.team_help")}
            items={equipe}
            accent="text-slate-700"
          />
        </div>
      )}
    </section>
  );
}
